import { ref, computed, onMounted, onBeforeUnmount, type Ref } from 'vue';

export interface ZoomPanOptions {
  minScale?: number;
  maxScale?: number;
  zoomSpeed?: number;
  initialScale?: number;
  initialOffsetX?: number;
  initialOffsetY?: number;
  enableKeyboard?: boolean;
  enableWheel?: boolean;
  enablePinch?: boolean;
  enablePan?: boolean;
  enableTouchPan?: boolean;
  panOnLeftClick?: boolean;
  /** Set true when the fit scale is the whole diagram in view (a layout canvas):
   *  at that scale the drawing is pinned to its fit offsets and no pan gesture
   *  starts — there is nothing off-screen to drag towards. */
  lockPanAtFit?: boolean;
  /** Reference (logical) canvas width in pixels. When provided, clientToLayout uses this
   *  instead of the container width, giving correct % coords for scaled-down canvases. */
  canvasWidth?: number;
  /** Reference (logical) canvas height in pixels. */
  canvasHeight?: number;
  /** Set false when the caller drives its own touch gestures and owns the
   *  container's `touch-action` (skips this composable's inline style). */
  manageTouchAction?: boolean;
}

export interface ZoomPanState {
  scale: Ref<number>;
  offsetX: Ref<number>;
  offsetY: Ref<number>;
  isPanning: Ref<boolean>;
  transformStyle: Ref<string>;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;
  fitToContainer: () => void;
  fitToScreen: () => void;
  clientToLayout: (clientX: number, clientY: number) => { x: number; y: number } | null;
  setZoom: (scale: number, offsetX: number, offsetY: number) => void;
  /** Current stored fit scale (scale that fits canvas in the container). */
  fitScale: Ref<number>;
  /** Tell the composable the (possibly updated) reference canvas dimensions */
  setCanvasDimensions: (width: number, height: number) => void;
  /** Store the "fit" state so fitToScreen() can snap back to it */
  setFitState: (scale: number, offsetX: number, offsetY: number) => void;
  /** Zoom toward a specific screen point (e.g. cursor position during wheel zoom) */
  zoomToPoint: (clientX: number, clientY: number, deltaScale: number) => void;
}

/**
 * Composable for zoom and pan functionality on a layout container
 * Supports mouse wheel zoom, pinch-to-zoom, panning, and keyboard shortcuts
 */
export function useZoomPan(containerRef: Ref<HTMLElement | null>, options: ZoomPanOptions = {}): ZoomPanState {
  const {
    minScale: minScaleInitial = 0.1,
    maxScale = 5.0,
    zoomSpeed = 0.1,
    initialScale = 1,
    initialOffsetX = 0,
    initialOffsetY = 0,
    enableKeyboard = true,
    enableWheel = true,
    enablePinch = true,
    enablePan = true,
    enableTouchPan = true,
    panOnLeftClick = false,
    lockPanAtFit = false,
    canvasWidth: initialCanvasWidth = 0,
    canvasHeight: initialCanvasHeight = 0,
    manageTouchAction = true,
  } = options;

  // State
  const scale = ref(initialScale);
  const offsetX = ref(initialOffsetX);
  const offsetY = ref(initialOffsetY);
  const isPanning = ref(false);

  // Dynamic min scale — updated to fitScale whenever setFitState is called
  const minScale = ref(minScaleInitial);

  // Reference canvas dimensions (updated via setCanvasDimensions)
  const canvasW = ref(initialCanvasWidth);
  const canvasH = ref(initialCanvasHeight);

  // Stored "fit" state for fitToScreen()
  const fitScale = ref(initialScale);
  const fitOffsetX = ref(initialOffsetX);
  const fitOffsetY = ref(initialOffsetY);

  // Pan state (mouse)
  const panStartX = ref(0);
  const panStartY = ref(0);
  const panStartOffsetX = ref(0);
  const panStartOffsetY = ref(0);

  // Touch pan state (single finger)
  const isTouchPanning = ref(false);
  const touchPanStartX = ref(0);
  const touchPanStartY = ref(0);
  const touchPanStartOffsetX = ref(0);
  const touchPanStartOffsetY = ref(0);

  // Pinch state
  const isPinching = ref(false);
  const pinchStartDistance = ref(0);
  const pinchStartScale = ref(1);

  // Computed CSS transform
  const transformStyle = computed(() => {
    return `translate(${offsetX.value}px, ${offsetY.value}px) scale(${scale.value})`;
  });

  /**
   * Clamp scale to min/max bounds
   */
  function clampScale(value: number): number {
    return Math.max(minScale.value, Math.min(maxScale, value));
  }

  /**
   * True at the fit scale — the state `lockPanAtFit` keeps still. The comparison
   * carries a hair of tolerance: a pinch or a wheel step lands on the same scale
   * as the fit without being bit-identical to it.
   */
  function atFitScale(currentScale: number): boolean {
    return lockPanAtFit && fitScale.value > 0 && currentScale <= fitScale.value * 1.001;
  }

  /**
   * Clamp offsets so the canvas stays accessible.
   * - When the rendered canvas is LARGER than the viewport: clamp edges to viewport boundaries.
   * - When the rendered canvas is SMALLER (zoomed out beyond fit): keep at least 50px of canvas
   *   visible so the user can always grab it again (no floating off-screen).
   * - When `lockPanAtFit` is set and the canvas sits at its fit: pin it to the fit
   *   offsets, so the whole diagram cannot be dragged out of centre.
   */
  function clampOffsets(newOffsetX: number, newOffsetY: number, currentScale: number): { x: number; y: number } {
    if (!containerRef.value) return { x: newOffsetX, y: newOffsetY };

    if (atFitScale(currentScale)) return { x: fitOffsetX.value, y: fitOffsetY.value };

    const rect = containerRef.value.getBoundingClientRect();
    const vpW = rect.width;
    const vpH = rect.height;

    // Rendered canvas size in screen pixels
    const refW = canvasW.value > 0 ? canvasW.value : vpW;
    const refH = canvasH.value > 0 ? canvasH.value : vpH;
    const renderedW = refW * currentScale;
    const renderedH = refH * currentScale;

    let minX: number, maxX: number, minY: number, maxY: number;

    if (renderedW >= vpW) {
      // Canvas overflows: clamp so canvas edges don't expose empty space
      minX = vpW - renderedW;
      maxX = 0;
    } else {
      // Canvas smaller than viewport: keep ≥50px visible on each axis
      const MARGIN = 50;
      minX = -(renderedW - MARGIN);
      maxX = vpW - MARGIN;
    }

    if (renderedH >= vpH) {
      minY = vpH - renderedH;
      maxY = 0;
    } else {
      const MARGIN = 50;
      minY = -(renderedH - MARGIN);
      maxY = vpH - MARGIN;
    }

    return {
      x: Math.max(minX, Math.min(maxX, newOffsetX)),
      y: Math.max(minY, Math.min(maxY, newOffsetY)),
    };
  }

  /**
   * Zoom in
   */
  function zoomIn() {
    const newScale = clampScale(scale.value + zoomSpeed);
    scale.value = newScale;

    // Clamp offsets after zoom
    const clamped = clampOffsets(offsetX.value, offsetY.value, newScale);
    offsetX.value = clamped.x;
    offsetY.value = clamped.y;
  }

  /**
   * Zoom out
   */
  function zoomOut() {
    const newScale = clampScale(scale.value - zoomSpeed);
    scale.value = newScale;

    // Clamp offsets after zoom
    const clamped = clampOffsets(offsetX.value, offsetY.value, newScale);
    offsetX.value = clamped.x;
    offsetY.value = clamped.y;
  }

  /**
   * Reset zoom to initial state (scale=1, origin)
   */
  function resetZoom() {
    scale.value = 1;
    offsetX.value = 0;
    offsetY.value = 0;
  }

  /**
   * Snap back to the stored fit state (fit-to-screen)
   */
  function fitToScreen() {
    scale.value = fitScale.value;
    offsetX.value = fitOffsetX.value;
    offsetY.value = fitOffsetY.value;
  }

  /**
   * Store the "fit" state for fitToScreen() to return to.
   */
  function setFitState(s: number, ox: number, oy: number) {
    fitScale.value = s;
    fitOffsetX.value = ox;
    fitOffsetY.value = oy;
    // Fit scale is the minimum — can't zoom out beyond fitting the canvas on screen
    minScale.value = s;
    // If currently more zoomed out than the new min, snap back to fit
    if (scale.value < s) {
      scale.value = s;
      const clamped = clampOffsets(offsetX.value, offsetY.value, s);
      offsetX.value = clamped.x;
      offsetY.value = clamped.y;
    }
  }

  /**
   * Update the reference canvas dimensions used for coordinate conversion.
   */
  function setCanvasDimensions(width: number, height: number) {
    canvasW.value = width;
    canvasH.value = height;
  }

  /**
   * Fit layout to container (alias to fitToScreen for backward compat)
   */
  function fitToContainer() {
    fitToScreen();
  }

  /**
   * Set zoom and pan values directly
   */
  function setZoom(newScale: number, newOffsetX: number, newOffsetY: number) {
    scale.value = clampScale(newScale);
    const clamped = clampOffsets(newOffsetX, newOffsetY, scale.value);
    offsetX.value = clamped.x;
    offsetY.value = clamped.y;
  }

  /**
   * Zoom toward a specific point (e.g., cursor position during wheel zoom)
   */
  function zoomToPoint(clientX: number, clientY: number, deltaScale: number) {
    if (!containerRef.value) return;

    const rect = containerRef.value.getBoundingClientRect();

    // Mouse position relative to container
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;

    // Point in layout space before zoom
    const layoutX = (mouseX - offsetX.value) / scale.value;
    const layoutY = (mouseY - offsetY.value) / scale.value;

    // New scale
    const newScale = clampScale(scale.value + deltaScale);

    // Adjust offset to keep the same layout point under the mouse
    const newOffsetX = mouseX - layoutX * newScale;
    const newOffsetY = mouseY - layoutY * newScale;

    scale.value = newScale;
    const clamped = clampOffsets(newOffsetX, newOffsetY, newScale);
    offsetX.value = clamped.x;
    offsetY.value = clamped.y;
  }

  /**
   * Convert client coordinates to canvas pixel coordinates.
   * Accounts for zoom scale and pan offset.
   */
  function clientToLayout(clientX: number, clientY: number): { x: number; y: number } | null {
    if (!containerRef.value) return null;

    const rect = containerRef.value.getBoundingClientRect();

    // Client position relative to container
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;

    // Position in canvas pixel space
    const x = (relX - offsetX.value) / scale.value;
    const y = (relY - offsetY.value) / scale.value;

    return { x, y };
  }

  /**
   * Handle mouse wheel zoom
   */
  function handleWheel(event: WheelEvent) {
    if (!enableWheel) return;

    // Only zoom if Ctrl is not pressed (to allow browser zoom)
    if (event.ctrlKey) return;

    event.preventDefault();

    const delta = -Math.sign(event.deltaY) * zoomSpeed;
    zoomToPoint(event.clientX, event.clientY, delta);
  }

  /**
   * Handle pan start (middle mouse, shift+left, or left click when panOnLeftClick enabled)
   */
  function handlePanStart(event: MouseEvent) {
    if (!enablePan) return;

    // At the fit the whole diagram is in view: a drag must not move it.
    if (atFitScale(scale.value)) return;

    const isMiddleMouse = event.button === 1;
    const isShiftLeft = event.button === 0 && event.shiftKey;
    const isLeftClick = event.button === 0 && panOnLeftClick;

    if (isMiddleMouse || isShiftLeft || isLeftClick) {
      event.preventDefault();
      isPanning.value = true;
      panStartX.value = event.clientX;
      panStartY.value = event.clientY;
      panStartOffsetX.value = offsetX.value;
      panStartOffsetY.value = offsetY.value;
    }
  }

  /**
   * Handle pan move
   */
  function handlePanMove(event: MouseEvent) {
    if (!isPanning.value) return;

    event.preventDefault();

    const deltaX = event.clientX - panStartX.value;
    const deltaY = event.clientY - panStartY.value;

    const newOffsetX = panStartOffsetX.value + deltaX;
    const newOffsetY = panStartOffsetY.value + deltaY;

    const clamped = clampOffsets(newOffsetX, newOffsetY, scale.value);
    offsetX.value = clamped.x;
    offsetY.value = clamped.y;
  }

  /**
   * Handle pan end
   */
  function handlePanEnd() {
    isPanning.value = false;
  }

  /**
   * Calculate distance between two touch points
   */
  function getTouchDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Get midpoint between two touch points
   */
  function getTouchMidpoint(touch1: Touch, touch2: Touch): { x: number; y: number } {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
  }

  /**
   * Handle touch start for pinch-to-zoom and single-finger pan
   */
  function handleTouchStart(event: TouchEvent) {
    if (event.touches.length === 2 && enablePinch) {
      // Cancel any single-touch pan
      isTouchPanning.value = false;

      event.preventDefault();
      isPinching.value = true;

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      pinchStartDistance.value = getTouchDistance(touch1, touch2);
      pinchStartScale.value = scale.value;
    } else if (event.touches.length === 1 && enableTouchPan && !atFitScale(scale.value)) {
      // Single finger pan
      isTouchPanning.value = true;
      const touch = event.touches[0];
      touchPanStartX.value = touch.clientX;
      touchPanStartY.value = touch.clientY;
      touchPanStartOffsetX.value = offsetX.value;
      touchPanStartOffsetY.value = offsetY.value;
    }
  }

  /**
   * Handle touch move for pinch-to-zoom and single-finger pan
   */
  function handleTouchMove(event: TouchEvent) {
    if (event.touches.length === 2 && isPinching.value) {
      event.preventDefault();

      const touch1 = event.touches[0];
      const touch2 = event.touches[1];

      const currentDistance = getTouchDistance(touch1, touch2);
      const scaleRatio = currentDistance / pinchStartDistance.value;
      const newScale = clampScale(pinchStartScale.value * scaleRatio);

      // Zoom toward midpoint between fingers
      const midpoint = getTouchMidpoint(touch1, touch2);

      if (!containerRef.value) return;
      const rect = containerRef.value.getBoundingClientRect();

      const mouseX = midpoint.x - rect.left;
      const mouseY = midpoint.y - rect.top;

      const layoutX = (mouseX - offsetX.value) / scale.value;
      const layoutY = (mouseY - offsetY.value) / scale.value;

      const newOffsetX = mouseX - layoutX * newScale;
      const newOffsetY = mouseY - layoutY * newScale;

      scale.value = newScale;
      const clamped = clampOffsets(newOffsetX, newOffsetY, newScale);
      offsetX.value = clamped.x;
      offsetY.value = clamped.y;
    } else if (event.touches.length === 1 && isTouchPanning.value) {
      event.preventDefault();
      const touch = event.touches[0];
      const deltaX = touch.clientX - touchPanStartX.value;
      const deltaY = touch.clientY - touchPanStartY.value;

      const newOffsetX = touchPanStartOffsetX.value + deltaX;
      const newOffsetY = touchPanStartOffsetY.value + deltaY;

      const clamped = clampOffsets(newOffsetX, newOffsetY, scale.value);
      offsetX.value = clamped.x;
      offsetY.value = clamped.y;
    }
  }

  /**
   * Handle touch end
   */
  function handleTouchEnd(event: TouchEvent) {
    if (event.touches.length < 2) {
      isPinching.value = false;
    }
    if (event.touches.length === 0) {
      isTouchPanning.value = false;
    }
  }

  /**
   * Handle keyboard shortcuts
   */
  function handleKeyDown(event: KeyboardEvent) {
    if (!enableKeyboard) return;

    // Ctrl+0: Fit to screen
    if (event.ctrlKey && event.key === '0') {
      event.preventDefault();
      fitToScreen();
    }

    // Ctrl++: Zoom in
    if (event.ctrlKey && (event.key === '+' || event.key === '=')) {
      event.preventDefault();
      zoomIn();
    }

    // Ctrl+-: Zoom out
    if (event.ctrlKey && event.key === '-') {
      event.preventDefault();
      zoomOut();
    }
  }

  // Mount and unmount
  onMounted(() => {
    if (!containerRef.value) return;

    const container = containerRef.value;

    // Add event listeners
    container.addEventListener('wheel', handleWheel as EventListener, { passive: false });
    container.addEventListener('mousedown', handlePanStart);
    document.addEventListener('mousemove', handlePanMove);
    document.addEventListener('mouseup', handlePanEnd);

    container.addEventListener('touchstart', handleTouchStart as EventListener, { passive: false });
    container.addEventListener('touchmove', handleTouchMove as EventListener, { passive: false });
    container.addEventListener('touchend', handleTouchEnd as EventListener);

    if (enableKeyboard) {
      document.addEventListener('keydown', handleKeyDown);
    }

    // Only suppress native touch gestures when touch interactions are enabled.
    if (manageTouchAction) {
      container.style.touchAction = enablePinch || enableTouchPan ? 'none' : 'auto';
    }
  });

  onBeforeUnmount(() => {
    if (!containerRef.value) return;

    const container = containerRef.value;

    // Remove event listeners
    container.removeEventListener('wheel', handleWheel as EventListener);
    container.removeEventListener('mousedown', handlePanStart);
    document.removeEventListener('mousemove', handlePanMove);
    document.removeEventListener('mouseup', handlePanEnd);

    container.removeEventListener('touchstart', handleTouchStart as EventListener);
    container.removeEventListener('touchmove', handleTouchMove as EventListener);
    container.removeEventListener('touchend', handleTouchEnd as EventListener);

    if (enableKeyboard) {
      document.removeEventListener('keydown', handleKeyDown);
    }

    if (manageTouchAction) {
      container.style.touchAction = '';
    }
  });

  return {
    scale,
    offsetX,
    offsetY,
    isPanning,
    transformStyle,
    zoomIn,
    zoomOut,
    resetZoom,
    fitToContainer,
    fitToScreen,
    fitScale,
    clientToLayout,
    setZoom,
    setCanvasDimensions,
    setFitState,
    zoomToPoint,
  };
}
